// Update ProcedureType to match database schema
export interface ProcedureType {
  id: string;
  slug: string;
  name: string;
  category: string;
  description?: string | null;
  display_order: number;
  /** Backend canonical specialty enum when provided by the specialties API. */
  enum_slug?: string | null;
}

/** Normalize API slugs for lookup (lowercase, underscores → hyphens). */
function normalizeSpecialtySlugKey(slug: string): string {
  return slug.trim().toLowerCase().replace(/_/g, "-");
}

// Map specialty slugs from database to backend enum values
export const SPECIALTY_SLUG_TO_ENUM_MAPPING: Record<string, string> = {
  // Active slugs
  'vascular': 'vascular',
  'fertility': 'fertility',
  'bariatric': 'bariatric',
  'wellness': 'wellness_healthcare',
  'hair-transplant': 'hair_transplant',
  'hair_transplant': 'hair_transplant',
  'plastic-surgery': 'plastic_surgery',
  'diagnostics-imaging': 'diagnostics_imaging',
  'dentistry': 'dentistry',
  'optical': 'optical',
  'stem-cells': 'stem_cells',
  'stem_cells': 'stem_cells',
  'wellness-healthcare': 'wellness_healthcare',
  'wellness_healthcare': 'wellness_healthcare',
  'bariatric-surgery': 'bariatric',
  'orthopedic': 'orthopedic',
  'orthopedics': 'orthopedic',
  'neuro-surgery': 'neuro_surgery',
  'ent': 'ear_nose_throat',
  'ear-nose-throat': 'ear_nose_throat',
  'ear-nose-and-throat': 'ear_nose_throat',
  'ear_nose_throat': 'ear_nose_throat',
  'urology': 'urology',
  // Legacy slugs mapped to the new enums
  'cosmetic-surgery': 'plastic_surgery',
  'cosmetic': 'plastic_surgery',
  'diagnosis-testing': 'diagnostics_imaging',
  'diagnosis_testing': 'diagnostics_imaging',
  'optics': 'optical',
  'general-medicine': 'diagnostics_imaging',
  'general': 'diagnostics_imaging',
};

const SPECIALTY_BACKEND_ENUMS = new Set(
  Object.values(SPECIALTY_SLUG_TO_ENUM_MAPPING),
);

// Legacy category mapping for backward compatibility (if needed)
export type SpecialtyCategory = 
  | 'vascular'
  | 'fertility'
  | 'bariatric'
  | 'wellness'
  | 'hair-transplant'
  | 'plastic-surgery'
  | 'diagnostics-imaging'
  | 'dentistry'
  | 'optical';

// Legacy mapping kept for backward compatibility but deprecated
export const SPECIALTY_TAG_MAPPING: Record<string, string> = {
  'vascular': 'vascular',
  'fertility': 'fertility',
  'hair-transplant': 'hair_transplant',
  'plastic-surgery': 'plastic_surgery',
  'diagnostics-imaging': 'diagnostics_imaging',
  'dentistry': 'dentistry',
  'optical': 'optical',
};

// Category display names (kept for backward compatibility)
export const CATEGORY_LABELS: Record<string, string> = {
  'vascular': 'Vascular',
  'fertility': 'Fertility',
  'wellness': 'Wellness',
  'hair-transplant': 'Hair Transplant',
  'hair_transplant': 'Hair Transplant',
  'plastic-surgery': 'Plastic Surgery',
  'plastic_surgery': 'Plastic Surgery',
  'diagnostics-imaging': 'Diagnostics / Imaging',
  'diagnostics_imaging': 'Diagnostics / Imaging',
  'dentistry': 'Dentistry',
  'optical': 'Optical',
  'stem-cells': 'Stem Cells',
  'stem_cells': 'Stem Cells',
  'wellness-healthcare': 'Wellness & Healthcare',
  'wellness_healthcare': 'Wellness & Healthcare',
  'bariatric': 'Bariatric Surgery',
  'bariatric-surgery': 'Bariatric Surgery',
  'orthopedic': 'Orthopedics',
  'orthopedics': 'Orthopedics',
  'neuro-surgery': 'Neuro Surgery',
  'neuro_surgery': 'Neuro Surgery',
};

/**
 * Maps a specialty slug to the backend enum value
 * @param slug - The specialty slug from the database
 * @returns The backend enum value, defaults to 'general' if not found
 */
export function mapSpecialtySlugToEnum(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed) return "general";

  const normalized = normalizeSpecialtySlugKey(trimmed);
  const mapped = SPECIALTY_SLUG_TO_ENUM_MAPPING[normalized];
  if (mapped) return mapped;

  const rawLower = trimmed.toLowerCase();
  if (SPECIALTY_BACKEND_ENUMS.has(rawLower)) return rawLower;

  // Heuristic mapping for wellness-related therapy slugs.
  // This lets the backend receive a stable enum/category even when slugs are more specific.
  const s = normalized;
  if (
    s.includes("wellness") ||
    s.includes("acupuncture") ||
    s.includes("colema") ||
    s.includes("hyperbaric") ||
    s.includes("massage") ||
    s.includes("reflexology") ||
    s.includes("ozone") ||
    s.includes("functional") ||
    s.includes("quantum") ||
    s.includes("iv") ||
    s.includes("yoga") ||
    s.includes("chinese")
  ) {
    return "wellness_healthcare";
  }

  // Backend validates specialty against enum values; unknown slugs must map
  // to a safe enum instead of being sent raw.
  return "general";
}

/**
 * Helper function to get specialty tag for API from a ProcedureType
 * @param procedure - The selected procedure/specialty
 * @returns The backend enum value for the specialty
 */
export function getSpecialtyTag(procedure: ProcedureType | string | null | undefined): string {
  if (!procedure) return "general";

  if (typeof procedure === "string") {
    return mapSpecialtySlugToEnum(procedure);
  }

  const enumSlug = procedure.enum_slug?.trim();
  if (enumSlug) {
    return mapSpecialtySlugToEnum(enumSlug);
  }

  if (procedure.slug) {
    return mapSpecialtySlugToEnum(procedure.slug);
  }

  if (procedure.category) {
    return mapSpecialtySlugToEnum(procedure.category);
  }

  return "general";
}

// Legacy exports - deprecated but kept for compatibility
export const ALL_PROCEDURES: ProcedureType[] = []; // Empty - will be populated from API
